import { ChangeEvent, useMemo } from "react";
import { NumericFormat } from "react-number-format";
import { FormikErrors } from "formik";
import { useQueryClient } from "@tanstack/react-query";
import { MEP_SENEBI_THRESHOLD } from "../../constants";

// TYPES
import { MepQuotes, OperateFormValues } from "../../types";

// UTILS
import { defaultNumericFormatProps } from "../../../../utils";
import {
  cleanInputString,
  formatCurrency,
  trunc,
} from "../../utils";

// UI
import { Card } from "@nextui-org/react";
import Skeleton from "../../../Skeleton/Skeleton";

// STYLES
import styles from "./AmountSelectMobile.module.scss";

interface Props {
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean | undefined
  ) => Promise<void> | Promise<FormikErrors<OperateFormValues>>;
  isBuyingSide: boolean;
  operationValueArs: string;
  operationValueMep: string;
  isTotal: boolean;
  availableArs: string;
  availableMep: string;
  setValues: (
    values: React.SetStateAction<OperateFormValues>,
    shouldValidate?: boolean
  ) => Promise<FormikErrors<OperateFormValues>> | Promise<void>;
  resetFields: () => void;
  currencySelected: "ARS" | "MEP";
  symbolPositionLoading?: boolean;
}

const AmountSelectMobile = ({
  setFieldValue,
  isBuyingSide,
  operationValueArs,
  operationValueMep,
  isTotal,
  availableArs,
  availableMep,
  setValues,
  resetFields,
  currencySelected,
  symbolPositionLoading,
}: Props) => {
  const {
    container,
    itemWrapper,
    inputHeader,
    available,
    inputText,
    inputWrapper,
    inputContainer,
    amountValue,
    hasValue,
    noValue,
    investAllContainer,
    checkbox,
    disabledText,
    sideContainer,
    sideButton,
    sideButtonActive,
    oppositeResult,
  } = styles;

  const queryClient = useQueryClient();
  const mepQuotes = queryClient.getQueryData<MepQuotes>(["mepPrice"]);

  const isDisabled = isBuyingSide
    ? Number(availableArs) <= 0
    : Number(availableMep) <= 0;
  const isArsSelected = currencySelected === "ARS";

  // -------- On value change -------- //

  const onValueChange = (currencyCode: "ARS" | "MEP", inputValue: string) => {
    if (!inputValue) {
      setValues(
        (prevValues) => ({
          ...prevValues,
          operationValueArs: "",
          operationValueMep: "",
          isTotal: false,
        }),
        true
      );
      return;
    }

    let field = "";
    let oppositeField = "";
    if (currencyCode === "ARS") {
      field = "operationValueArs";
      oppositeField = "operationValueMep";
      inputValue = inputValue.replace("$", "");
    } else {
      field = "operationValueMep";
      oppositeField = "operationValueArs";
      inputValue = inputValue.replace("U$S", "");
    }

    let floatValue = trunc(Number(cleanInputString(inputValue)));

    const pptPrice = isBuyingSide ? mepQuotes?.PPT?.BUY : mepQuotes?.PPT?.SELL;
    const senebiPrice = isBuyingSide ? mepQuotes?.SENEBI?.BUY : mepQuotes?.SENEBI?.SELL;
    const usdForThreshold = currencyCode === "MEP" ? floatValue : floatValue / (pptPrice || 1);
    const mepPriceValue = (usdForThreshold < MEP_SENEBI_THRESHOLD ? pptPrice : senebiPrice) ?? 0;

    let result = "";
    let isTotal = false;

    if (currencyCode === "ARS") {
      isTotal = floatValue === trunc(Number(availableArs));
      if (isTotal) floatValue = Number(availableArs);
      if (!isNaN(floatValue) && mepPriceValue > 0)
        result = (floatValue / mepPriceValue).toString();
    } else {
      isTotal = floatValue === trunc(Number(availableMep));
      if (isTotal) floatValue = Number(availableMep);
      if (!isNaN(floatValue)) result = (floatValue * mepPriceValue).toString();
    }

    setValues(
      (prevValues) => ({
        ...prevValues,
        [field]: floatValue.toString(),
        [oppositeField]: result,
        isTotal: isTotal,
      }),
      true
    );
  };

  const getCheckboxText = () => {
    return isBuyingSide ? "Invertir todo" : "Vender todo";
  };

  // -------- Checkbox change -------- //

  const onCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue("isTotal", event.target.checked);
    if (event.target.checked) {
      if (isBuyingSide) {
        onValueChange("ARS", formatCurrency(Number(availableArs)));
      } else {
        onValueChange("MEP", formatCurrency(Number(availableMep)));
      }
    } else {
      resetFields();
    }
  };

  // -------- Aux components -------- //

  const CurrencySelector = useMemo(
    () => (
      <div className={sideContainer}>
        <div
          onClick={() => {
            setFieldValue("currencySelected", "ARS", true);
            resetFields();
          }}
          className={`${sideButton} ${isArsSelected ? sideButtonActive : ""}`}
        >
          ARS
        </div>
        <div
          onClick={() => {
            setFieldValue("currencySelected", "MEP", true);
            resetFields();
          }}
          className={`${sideButton} ${!isArsSelected ? sideButtonActive : ""}`}
        >
          MEP
        </div>
      </div>
    ),
    [currencySelected, isArsSelected]
  );

  const MainCurrencyInput = useMemo(() => {
    const value = isArsSelected ? operationValueArs : operationValueMep;
    const prefix = isArsSelected ? "$" : "U$S ";
    const placeholder = `${prefix}0,00`;
    return (
      <div className={amountValue}>
        <NumericFormat
          {...defaultNumericFormatProps}
          displayType="input"
          onChange={(e) => onValueChange(currencySelected, e.target.value)}
          value={value}
          placeholder={placeholder}
          type="text"
          inputMode="decimal"
          prefix={prefix}
          className={`${inputText} ${inputWrapper} ${
            value ? hasValue : noValue
          }`}
          onFocus={(e) => {
            // Wait for padding to be applied and DOM to update
            setTimeout(() => {
              const inputElement = e.target as HTMLElement;

              let parent = inputElement.parentElement;
              while (parent) {
                if (parent.classList.contains("bodyMobileContainer")) {
                  const scrollContainer = parent;
                  // Use requestAnimationFrame to ensure DOM is updated
                  requestAnimationFrame(() => {
                    // Small additional delay for padding to be applied
                    setTimeout(() => {
                      scrollContainer.scrollTo({
                        top: scrollContainer.scrollHeight,
                        behavior: "smooth",
                      });
                    }, 100);
                  });
                  return;
                }
                parent = parent.parentElement;
              }
            }, 300);
          }}
        />
      </div>
    );
  }, [currencySelected, operationValueArs, operationValueMep, onValueChange]);

  const OppositeCurrencyResult = useMemo(() => {
    const oppositeCurrency = isArsSelected ? "MEP" : "ARS";
    const currency = isArsSelected ? operationValueMep : operationValueArs;
    const prefix = isArsSelected ? "U$S " : "$";
    return (
      <div className={oppositeResult}>
        <b>
          <NumericFormat
            {...defaultNumericFormatProps}
            value={currency || "0,00"}
            prefix={prefix}
          />
        </b>
        <span>{oppositeCurrency}</span>
      </div>
    );
  }, [currencySelected, operationValueArs, operationValueMep]);

  return (
    <Card className={container} shadow="none">
      {CurrencySelector}

      <div className={inputHeader}>Ingresá el monto en {currencySelected}</div>

      <div className={itemWrapper}>
        <div className={inputContainer}>
          {MainCurrencyInput}
          {OppositeCurrencyResult}
        </div>
        <div className={available}>
          {symbolPositionLoading ? (
            <Skeleton width="30%" height="20px" />
          ) : (
            <>
              <span>Disponible</span>{" "}
              <b>
                <NumericFormat
                  {...defaultNumericFormatProps}
                  value={isBuyingSide ? availableArs : availableMep}
                  prefix={isBuyingSide ? "$" : "U$S "}
                />
              </b>
              <span> {isBuyingSide ? "ARS" : "MEP"}</span>
            </>
          )}
        </div>

        <div
          className={`${investAllContainer} ${isDisabled ? disabledText : ""}`}
        >
          <input
            type="checkbox"
            className={checkbox}
            checked={isTotal}
            onChange={onCheckboxChange}
            disabled={isDisabled}
          />
          <span>{getCheckboxText()}</span>
        </div>
      </div>
    </Card>
  );
};

export default AmountSelectMobile;
